import React from 'react';

import { Link } from 'react-router-dom';

import {
	Box,
	createStyles,
	TFDataTable,
	TFText,
	UnstyledButton,
	useDebouncedValue,
	useLocalStorage,
	usePersistentPagination,
} from '@tf/ui';
import { fmt, M, S, splitObjectId } from '@tf/utils';

import { useOverviewData } from '@/core/api/overview';
import type { SortingParams, SortingType } from '@/core/api/payments';
import { useAllPayments } from '@/core/api/payments';
import type { PaymentDecisionStatus, PaymentStatus, PaymentWithProfile } from '@/core/types/payments';
import { getDisplayName } from '@/core/utils';
import { AssignedPaymentManagersWidget } from '@/components/payments';
import { TFBadge, TFBadgeColors } from '@/components/shared';
import { orderTypesMap } from '@/components/utils';

const riskLevelColors: Record<string, TFBadgeColors> = {
	HIGH: TFBadgeColors.ORANGE,
	MEDIUM: TFBadgeColors.YELLOW,
	LOW: TFBadgeColors.GREEN,
};

const statusColors: Record<PaymentStatus | PaymentDecisionStatus, string> = {
	UNKNOWN: TFBadgeColors.DIMMED,
	PENDING_CHECK: TFBadgeColors.DIMMED,
	PENDING_REVIEW: TFBadgeColors.BLUE,
	PENDING_DETAILS: TFBadgeColors.BLUE,
	APPROVED: TFBadgeColors.GREEN,
	REJECTED: TFBadgeColors.RED,
	APPROVED_ERROR: TFBadgeColors.DIMMED,
	REJECTED_ERROR: TFBadgeColors.DIMMED,
	ORDER_COMPLETED: TFBadgeColors.GREEN,
	ORDER_REJECTED: TFBadgeColors.RED,
	FAILED: TFBadgeColors.RED,
};

const useStyles = createStyles(({ colors }) => ({
	link: {
		fontSize: 13,
		height: 32,
		display: 'flex',
		alignItems: 'center',
		color: colors.brand[6],
		'&:hover': {
			color: colors.brand[6],
			textDecoration: 'underline',
		},
	},
}));

const sortingLabelsMap: Record<string, { name: string; type: SortingType }> = {
	order_id: { name: 'order_id', type: 'string' },
	order_type: { name: 'order_type', type: 'string' },
	amount: { name: 'amount', type: 'number' },
	client_id: { name: 'client_id', type: 'string' },
	'@status': { name: '@status', type: 'string' },
	risk: { name: 'risk', type: 'string' },
};

const sortingMetaMap: Record<string, { name: string; type: SortingType }> = {
	'@createdAt': { name: '@createdAt', type: 'date' },
};

export const PaymentsTable = ({ objectUri }: { objectUri?: string }) => {
	const { data: overviewData } = useOverviewData();
	const listUsers = overviewData?.listUsers;

	const { classes } = useStyles();

	const [pagination, setPagination] = usePersistentPagination('payments-table', {
		pageSize: 10,
		pageIndex: 0,
	});
	const paginationParams = {
		offset: pagination.pageIndex * pagination.pageSize,
		limit: pagination.pageSize,
	};

	const [sortingState, setSortingState] = useLocalStorage<
		{
			desc: boolean;
			id: string;
		}[]
	>({
		defaultValue: [{ desc: true, id: '@createdAt' }],
		key: 'payments-table-sorting',
		getInitialValueInEffect: false,
	});

	const getSortingParams = (): SortingParams | undefined => {
		if (!sortingState[0]) return undefined;

		const { id, desc } = sortingState[0];
		const label = sortingLabelsMap[id];
		const meta = sortingMetaMap[id];

		return {
			sortByLabels: label?.name,
			sortByMeta: meta?.name,
			sortByDirection: desc ? 'DESC' : 'ASC',
			sortByType: label?.type ?? meta?.type,
		};
	};
	const sortingParams = getSortingParams();

	const [filtersState, setFiltersState] = useLocalStorage<
		{
			id: string;
			value: unknown;
		}[]
	>({
		defaultValue: [],
		key: 'payments-table-filters',
		getInitialValueInEffect: false,
	});

	const getFilteringParams = () => {
		const params =
			filtersState.length > 0
				? filtersState.map((filter) => ({ path: `/${filter.id}`, value: String(filter.value) }))
				: [];

		if (objectUri) {
			params.push({ path: '/@with/profile', value: objectUri });
		}
		return params.length ? params : undefined;
	};

	const [searchValue, setSearchValue] = useLocalStorage<string>({
		defaultValue: '',
		key: 'payments-table-search',
		getInitialValueInEffect: false,
	});
	const [debouncedSearch] = useDebouncedValue(searchValue, 500);

	const { data: requestResult, isPending } = useAllPayments(
		paginationParams,
		sortingParams,
		getFilteringParams(),
		debouncedSearch
	);

	const payments = {
		totalCount: requestResult?.ok ? Number(requestResult.response.headers.get('total-results')) : 0,
		items: requestResult?.ok ? requestResult.data.get : [],
	};

	return (
		<Box mt="12px">
			<TFDataTable<PaymentWithProfile>
				name="payments-list"
				isLoading={isPending}
				serverSorting={{
					onSortingChange: setSortingState,
					state: sortingState,
				}}
				serverFiltering={{
					onFiltersChange: setFiltersState,
					state: filtersState,
				}}
				serverPagination={
					payments.items.length > 0
						? {
								...pagination,
								rowCount: payments.totalCount,
								onPaginationChange: setPagination,
						  }
						: undefined
				}
				serverSearch={{
					state: searchValue,
					onSearchChange: setSearchValue,
				}}
				searchPlaceholder="Search by Order ID, Client ID"
				data={payments.items ?? []}
				defs={[
					{
						header: 'Order ID',
						accessorKey: 'order_id',
						size: 200,
						enableSorting: true,
						Cell: ({ cell }) => {
							const { labels, id } = cell.row.original;
							const { objectName: paymentUuid } = splitObjectId(id);

							return (
								<UnstyledButton
									component={Link}
									to={`/payments/${paymentUuid}`}
									className={classes.link}
								>
									<TFText inherit>{labels.order_id || 'N/A'}</TFText>
								</UnstyledButton>
							);
						},
					},
					{
						header: 'Order type',
						accessorKey: 'order_type',
						size: 115,
						enableSorting: true,
						Cell: ({ cell }) => {
							const { labels, id } = cell.row.original;
							const isInward = id.includes('inward');
							return (
								<TFText inherit lineClamp={1}>
									{`${orderTypesMap[labels?.order_type]}, ${isInward ? 'Inward' : 'Outward'}` || 'N/A'}
								</TFText>
							);
						},
					},
					{
						header: 'Amount',
						accessorKey: 'amount',
						size: 120,
						enableSorting: true,
						Cell: ({ cell }) => {
							const {
								id,
								labels: { amount },
							} = cell.row.original;
							const isInward = id.includes('inward');
							return (
								<TFText c={isInward ? '#15803D' : undefined} inherit lineClamp={1}>{`${
									isInward ? '+' : '-'
								}${M.format(amount)}`}</TFText>
							);
						},
					},
					{
						header: 'Currency',
						accessorKey: 'amount_currency',
						size: 115,
						enableSorting: false,
						Cell: ({ cell }) => {
							const {
								labels: { amount_currency },
							} = cell.row.original;
							return <TFText inherit>{amount_currency}</TFText>;
						},
					},
					{
						header: 'Client ID',
						accessorKey: 'client_id',
						enableSorting: false,
						Cell: ({ cell }) => {
							const { labels } = cell.row.original;
							return <div>{labels.client_id}</div>;
						},
					},
					{
						header: 'Account name',
						accessorKey: 'account_name',
						enableSorting: false,
						Cell: ({ cell }) => {
							const { nestedRelation } = cell.row.original;
							const accountObject = nestedRelation[0]?.object;
							return (
								<TFText inherit lineClamp={1}>
									{accountObject?.labels.name || 'N/A'}
								</TFText>
							);
						},
					},
					{
						header: 'Status',
						accessorKey: 'status',
						size: 135,
						enableSorting: false,
						Cell: ({ cell }) => {
							const { meta } = cell.row.original;
							const status = meta?.['@status'];
							if (!status)
								return (
									<TFText inherit c="dimmed">
										Not defined
									</TFText>
								);

							return (
								<TFText inherit c={statusColors[status]}>
									{S.prettify(status)}
								</TFText>
							);
						},
					},
					{
						header: 'Risk',
						accessorKey: 'risk',
						size: 115,
						enableSorting: false,
						Cell: ({ cell }) => {
							const {
								meta: { risk },
							} = cell.row.original;
							return (
								<TFBadge
									withBackground={false}
									text={risk ? S.prettify(risk) : 'Not set'}
									color={risk ? riskLevelColors[risk] : TFBadgeColors.DIMMED}
								/>
							);
						},
					},
					{
						header: 'Assignee',
						accessorKey: 'assignee',
						size: 130,
						enableSorting: false,
						Cell: ({ cell }) => {
							const {
								id,
								labels: { assigned_managers },
							} = cell.row.original;
							return (
								<AssignedPaymentManagersWidget
									managers={listUsers ?? []}
									paymentId={id}
									selectedIds={assigned_managers ?? []}
								/>
							);
						},
					},
					{
						header: 'Instruction received',
						minSize: 210,
						accessorKey: '@createdAt',
						Cell: ({ cell }) => {
							const { meta } = cell.row.original;
							return (
								<TFText inherit>
									{fmt.toDate(meta['@createdAt'], { preset: 'full_date_with_time' })}
								</TFText>
							);
						},
					},
				]}
				availableFilters={[
					{
						name: '@status',
						label: 'Status',
						type: 'select',
						value: [
							{ value: 'PENDING_CHECK', label: 'Pending check' },
							{ value: 'PENDING_REVIEW', label: 'Pending review' },
							{ value: 'PENDING_DETAILS', label: 'Pending details' },
							{ value: 'APPROVED', label: 'Approved' },
							{ value: 'REJECTED', label: 'Rejected' },
							{ value: 'ORDER_COMPLETED', label: 'Order completed' },
							{ value: 'ORDER_REJECTED', label: 'Order rejected' },
							{ value: 'FAILED', label: 'Failed' },
						],
					},
					{
						name: 'order_type',
						label: 'Order type',
						type: 'select',
						value: [
							{ value: 'CASH_IN', label: 'First-party, Inward' },
							{ value: 'CASH_OUT', label: 'First-party, Outward' },
							{ value: 'PAYMENT_IN', label: 'Third-party, Inward' },
							{ value: 'PAYMENT_OUT', label: 'Third-party, Outward' },
						],
					},
					{
						name: 'risk',
						type: 'select',
						label: 'Risk',
						value: [
							{ value: 'LOW', label: 'Low' },
							{ value: 'MEDIUM', label: 'Medium' },
							{ value: 'HIGH', label: 'High' },
						],
					},
					{
						name: 'assigned_managers',
						type: 'select',
						label: 'Assigned manager',
						value: [
							{ value: 'null', label: 'Not assigned' },
							...(listUsers?.map((user) => ({ value: user.userId, label: getDisplayName(user) })) || []),
						],
					},
				]}
			/>
		</Box>
	);
};
